import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Profil } from "@biloplader/calc";
import {
  beregnAlle,
  EGEN_ID,
  egenToDto,
  erUdloebet,
  fetchProducts,
  fetchRates,
  postForslag,
  produktLink,
  ratesToSatser,
  type Beregning,
  type EgenAftale,
  type EgenAftaleVedhaeft,
  type ProduktDto,
  type RatesDto,
} from "../../lib/api";
import { n, n2 } from "../../lib/format";
import { eraDate, kwhYearOf, useWizard } from "../wizard/WizardContext";
import ChargeSpinner from "../../components/ChargeSpinner";
import {
  IconAdvarsel,
  IconChevron,
  IconFlag,
  IconGenstart,
  IconLyn,
  IconPilNed,
  IconStjerne,
} from "../../components/icons";
import Compare from "../compare/Compare";
import { MAX_VALGTE } from "../compare/projection";
import EgenAftaleBox from "./EgenAftaleBox";
import FejlModal from "./FejlModal";

const EGEN_STORAGE = "egenAftale";

/** Deals shown before the "show more" button — enough to choose from without a wall of cards. */
const SYNLIGE_START = 5;
/** The "add your own deal" card sits after this many deals (0-based), i.e. as the third item. */
const EGEN_EFTER = 2;

function loadEgen(): EgenAftale | null {
  try {
    const raw = localStorage.getItem(EGEN_STORAGE);
    return raw ? (JSON.parse(raw) as EgenAftale) : null;
  } catch {
    return null;
  }
}

export default function Results() {
  const { t, i18n } = useTranslation();
  const { state } = useWizard();
  const lang = i18n.language;

  const [produkter, setProdukter] = useState<ProduktDto[]>([]);
  const [rates, setRates] = useState<RatesDto | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [sortBy, setSortBy] = useState<"price" | "binding">("price");
  const [open, setOpen] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [egen, setEgen] = useState<EgenAftale | null>(loadEgen);
  const [visUdloebne, setVisUdloebne] = useState(false);
  const [fejlFor, setFejlFor] = useState<ProduktDto | null>(null);
  // Products whose provider link was clicked — only then is "report an issue" offered.
  const [linkKlik, setLinkKlik] = useState<ReadonlySet<number>>(new Set());
  // id → chart colour slot. Keyed by product so a series keeps its colour when
  // the selection changes; the slot is only freed on deselect.
  const [valgtSlots, setValgtSlots] = useState<ReadonlyMap<number, number>>(new Map());
  const [visSammenligning, setVisSammenligning] = useState(false);
  const [visAlle, setVisAlle] = useState(false);

  const toggleValg = (id: number) =>
    setValgtSlots((prev) => {
      const next = new Map(prev);
      if (next.delete(id)) return next;
      if (next.size >= MAX_VALGTE) return prev;
      const brugte = new Set(next.values());
      let slot = 0;
      while (brugte.has(slot)) slot++;
      next.set(id, slot);
      return next;
    });

  const saveEgen = (e: EgenAftale, v: EgenAftaleVedhaeft) => {
    setEgen(e);
    try {
      localStorage.setItem(EGEN_STORAGE, JSON.stringify(e)); // numbers only — never the attachments
    } catch {
      /* ignore */
    }
    // Anonymous suggestion for the backoffice, incl. the user's terms/files.
    void postForslag(e, v);
  };
  const removeEgen = () => {
    setEgen(null);
    try {
      localStorage.removeItem(EGEN_STORAGE);
    } catch {
      /* ignore */
    }
  };

  // Refetch when the era (date), price area, expired-toggle or language changes —
  // not on every slider move.
  useEffect(() => {
    let alive = true;
    setStatus("loading");
    const date = eraDate(state.era);
    Promise.all([fetchProducts(lang, date, visUdloebne), fetchRates(lang, date, state.area)])
      .then(([p, r]) => {
        if (!alive) return;
        setProdukter(p);
        setRates(r);
        setStatus("ok");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
    };
  }, [state.era, state.area, lang, attempt, visUdloebne]);

  const profil = useMemo<Profil>(
    () => ({
      kmAar: state.km,
      whKm: state.wh,
      udeProcent: state.ude,
      laderOmNatten: state.night,
      harEgenBoks: state.setup === "box",
    }),
    [state.km, state.wh, state.ude, state.night, state.setup],
  );

  const rows = useMemo<Beregning[]>(() => {
    if (!rates || produkter.length === 0) return [];
    const alle = egen ? [...produkter, egenToDto(egen)] : produkter;
    const r = beregnAlle(profil, alle, ratesToSatser(rates));
    return sortBy === "binding"
      ? [...r].sort((a, b) => a.produkt.bindingsperiodeMdr - b.produkt.bindingsperiodeMdr || a.total - b.total)
      : r;
  }, [produkter, rates, sortBy, profil, egen]);

  // Drop selections whose product is no longer on the list (era change, own
  // agreement removed, expired ones hidden again). Ignored while rows are empty
  // so a refetch doesn't wipe the selection.
  useEffect(() => {
    if (rows.length === 0) return;
    const findes = new Set(rows.map((r) => r.produkt.produktversionId));
    setValgtSlots((prev) => {
      const next = new Map([...prev].filter(([id]) => findes.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  if (status === "loading")
    return (
      <div aria-busy="true" className="flex flex-col gap-3.5 py-4">
        <ChargeSpinner label={t("results.loading")} className="py-8" />
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            className="h-[86px] animate-pulse rounded-l border border-line bg-surface-sunk"
            style={{ animationDelay: `${i * 220}ms` }}
          />
        ))}
      </div>
    );
  if (status === "error")
    return (
      <div className="py-8 text-center">
        <p className="text-danger">{t("results.error")}</p>
        <button type="button" className="btn btn-ghost mt-4" onClick={() => setAttempt((a) => a + 1)}>
          <IconGenstart className="h-[17px] w-[17px]" />
          {t("results.retry")}
        </button>
      </div>
    );
  if (rows.length === 0) return <p className="py-8 text-center text-ink-soft">{t("results.empty")}</p>;

  const cheapest = Math.min(...rows.map((r) => r.total));
  const priciest = Math.max(...rows.map((r) => r.total));
  const eraLabel = state.era === "now" ? t("era.labelNow") : t("era.label2027");
  const yn = (v: boolean) => (v ? t("detail.ja") : t("detail.nej"));

  // Only the first handful is shown up front. The user's own agreement and
  // anything they have ticked for comparison always stay visible, so nothing
  // silently disappears behind the button — the rank badge keeps the true
  // position from the full list.
  const altidVist = (r: Beregning) =>
    r.produkt.produktversionId === EGEN_ID || valgtSlots.has(r.produkt.produktversionId);
  const synlige = visAlle
    ? rows.map((r, i) => ({ r, rang: i + 1 }))
    : rows
        .map((r, i) => ({ r, rang: i + 1 }))
        .filter(({ r }, i) => i < SYNLIGE_START || altidVist(r));
  const skjulte = rows.length - synlige.length;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-[clamp(24px,4vw,36px)]">{t("results.title")}</h2>
          <div className="mt-1.5 font-mono text-xs text-ink-faint">
            {t("results.profile", {
              km: n(state.km),
              wh: state.wh,
              kwh: n(kwhYearOf(state)),
              ude: state.ude,
              era: eraLabel,
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 font-mono text-[12.5px] text-ink-soft">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--accent)]"
              checked={visUdloebne}
              onChange={(e) => setVisUdloebne(e.target.checked)}
            />
            {t("results.visUdloebne")}
          </label>
          <select
            aria-label={t("results.sortLabel")}
            className="cursor-pointer rounded-full border border-line bg-surface px-3.5 py-2 font-mono text-[12.5px] text-ink"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "price" | "binding")}
          >
            <option value="price">{t("results.sortPrice")}</option>
            <option value="binding">{t("results.sortBind")}</option>
          </select>
        </div>
      </div>

      {/* Selection bar — appears above the list only once something is ticked,
          so it never occupies the fold for users who just want to browse.
          Not sticky: the wizard card clips with overflow-hidden (for its rounded
          corners), which kills position:sticky for anything inside it. The list
          is capped at five cards, so the bar stays within easy reach anyway. */}
      {valgtSlots.size > 0 && (
        <div className="mt-4 print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-l border border-line-strong bg-surface px-5 py-3.5 shadow-lift">
            <span className="font-mono text-[12.5px] text-ink-soft">
              {t("sam.valgt", { n: valgtSlots.size, maks: MAX_VALGTE })}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" className="btn btn-ghost px-4 py-2.5 text-[14px]" onClick={() => setValgtSlots(new Map())}>
                {t("sam.ryd")}
              </button>
              <button
                type="button"
                className="btn btn-primary px-5 py-2.5 text-[14px]"
                disabled={valgtSlots.size < 2}
                onClick={() => setVisSammenligning(true)}
              >
                {t("sam.knap")} →
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-3.5">
        {synlige.map(({ r, rang }, i) => {
          const p = r.produkt;
          const udloebet = p.produktversionId !== EGEN_ID && erUdloebet(p, eraDate(state.era));
          const best = r.total === cheapest && !udloebet;
          const save = priciest - r.total;
          // Bjaelkens laengde er aftalens aarlige pris som andel af den dyreste
          // paa listen. Foer blev spaendet mellem billigst og dyrest strakt ud
          // over 30 til 100 %, saa laengden ikke svarede til et tal man kunne
          // laese nogen steder. Nu er halvt saa lang lig med halvt saa dyr.
          const barPct = priciest === 0 ? 0 : Math.max(4, Math.round((r.total / priciest) * 100));
          const isOpen = open === p.produktversionId;
          const valgt = valgtSlots.has(p.produktversionId);
          const kanVaelges = valgt || valgtSlots.size < MAX_VALGTE;
          // The "add your own deal" card rides along as the third item — high
          // enough to be noticed, low enough not to push the best deals down.
          // On a very short list it lands after whatever the last card is.
          const egenEfterDenne = i === Math.min(EGEN_EFTER - 1, synlige.length - 1);
          return (
            <Fragment key={p.produktversionId}>
            <article
              className={`overflow-hidden rounded-l border bg-surface transition ${
                best ? "border-ink shadow-lift" : "border-line hover:border-accent-deep"
              } ${udloebet ? "opacity-75" : ""}`}
            >
              <div className="grid grid-cols-[1fr_auto] items-center gap-4 px-5 py-5 sm:grid-cols-[36px_1fr_120px_auto]">
                <div className="hidden font-display text-2xl font-semibold text-ink-faint sm:block">{rang}</div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[17px] font-bold">
                    {p.udbyder} {p.produkt}
                    {p.produktversionId === EGEN_ID && (
                      <span className="rounded-full border border-line-strong px-2.5 py-0.5 font-mono text-[10.5px] uppercase tracking-wide text-ink-soft">
                        {t("egen.tag")}
                      </span>
                    )}
                    {udloebet && (
                      <span className="rounded-full bg-surface-sunk px-2.5 py-0.5 font-mono text-[10.5px] uppercase tracking-wide text-ink-faint">
                        {t("results.udloebet", { til: p.gyldigTil })}
                      </span>
                    )}
                    {best && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 font-mono text-[10.5px] uppercase tracking-wide text-accent-ink">
                        <IconStjerne className="h-3 w-3" />
                        {t("results.best")}
                      </span>
                    )}
                  </div>
                  {p.udbyderKoncern && (
                    <div className="mt-1 text-[12.5px] text-ink-faint">
                      {t("results.koncern", { name: p.udbyderKoncern })}
                    </div>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-2 text-[12.5px] text-ink-faint">
                    <Tag>{p.prismodel === "spot" ? t("detail.spot") : t("detail.fast")}</Tag>
                    {p.offentligOpladningInkluderet && (
                      <Tag>
                        <IconLyn className="h-[11px] w-[11px] text-accent-deep" />
                        {t("detail.inkl")}
                      </Tag>
                    )}
                    <Tag>{p.bindingsperiodeMdr === 0 ? t("detail.ingen") : `${p.bindingsperiodeMdr} ${t("detail.mdr")}`}</Tag>
                  </div>
                </div>
                {/* Prisen staar i tekst lige ved siden af, saa bjaelken er ren
                    optik for skimmelaesning og skjules for skaermlaesere. */}
                <div className="hidden sm:block" aria-hidden="true">
                  <div className="h-2 overflow-hidden rounded-full bg-surface-sunk">
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ease-ease ${
                        best ? "bg-accent" : "bg-ink-faint"
                      }`}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <div className="num whitespace-nowrap text-2xl font-semibold leading-none text-ink">{n(r.total)}</div>
                  <small className="whitespace-nowrap font-mono text-[10.5px] uppercase tracking-wide text-ink-faint">
                    {t("results.perYear")}
                  </small>
                  {/* Egen linje under prisen. Paa en smal skaerm skal besparelsen
                      have lov at ombryde: uden ombrydning presser den
                      titelkolonnen sammen til et ord pr. linje. Fra sm er der
                      plads til den paa én linje. */}
                  {save > 0 && (
                    <div className="ml-auto mt-1 flex max-w-[16ch] items-start justify-end gap-1 text-xs font-semibold leading-tight text-accent-deep sm:max-w-none sm:whitespace-nowrap">
                      <IconPilNed className="mt-px h-3.5 w-3.5 shrink-0" />
                      <span>{t("results.save")} {n(save)} kr {t("results.vs")}</span>
                    </div>
                  )}
                </div>
              </div>

              {isOpen && (
                <div id={`pv-detail-${p.produktversionId}`} className="border-t border-line px-5 py-4">
                  <div className="grid grid-cols-1 gap-x-7 gap-y-2.5 text-[13.5px] sm:grid-cols-2">
                    <Row label={t("detail.prismodel")} value={p.prismodel === "spot" ? `${t("detail.spot")} ${n2(p.kwhTillaeg)}` : `${n2(p.fastprisKrKwh)} kr/kWh`} />
                    <Row label={t("detail.hjemme")} value={`${n2(r.homePrice)} kr/kWh`} />
                    <Row label={t("detail.ude")} value={`${n2(r.udePrice)} kr/kWh`} />
                    <Row label={t("detail.refusion")} value={r.effRefusion > 0 ? `−${n2(r.effRefusion)} kr/kWh` : t("detail.ingen")} />
                    <Row label={t("detail.abon")} value={`${n(p.maanedsabonnement)} kr/${t("detail.mdr")}`} />
                    <Row label={t("detail.boks")} value={p.ladeboksInkluderet ? t("detail.inkl") : yn(false)} />
                    <Row
                      label={t("detail.oprettelse")}
                      value={p.oprettelsesGebyr > 0 ? `${n(p.oprettelsesGebyr)} kr` : t("detail.ingen")}
                    />
                    <Row label={t("detail.install")} value={r.install > 0 ? `${n(r.install)} kr/år` : "0"} />
                    <Row label={t("detail.binding")} value={p.bindingsperiodeMdr === 0 ? t("detail.ingen") : `${p.bindingsperiodeMdr} ${t("detail.mdr")}`} />
                  </div>
                  {p.kraeverEgenBoks && (
                    <p className="mt-3 flex items-start gap-2 text-[13px] text-ink-soft">
                      <IconAdvarsel className="mt-px h-4 w-4 shrink-0 text-warning" />
                      <span>{t("detail.kraeverEgenBoks")}</span>
                    </p>
                  )}
                  {p.vilkaar?.resume && <p className="mt-3 text-[13.5px] text-ink-soft">{p.vilkaar.resume}</p>}
                  {p.produktversionId !== EGEN_ID && produktLink(p) && (
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                      <a
                        href={produktLink(p)!}
                        target="_blank"
                        rel={p.fremhaevetLink ? "noopener sponsored" : "noopener noreferrer"}
                        className={
                          p.fremhaevetLink
                            ? "btn btn-primary"
                            : "font-mono text-xs text-accent-deep underline-offset-2 hover:underline"
                        }
                        onClick={() => setLinkKlik((prev) => new Set(prev).add(p.produktversionId))}
                      >
                        {t("detail.goProvider")}
                      </a>
                      {linkKlik.has(p.produktversionId) && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 font-mono text-xs text-ink-faint underline-offset-2 hover:text-ink hover:underline"
                          onClick={() => setFejlFor(p)}
                        >
                          <IconFlag className="h-[15px] w-[15px]" />
                          {t("fejl.knap")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-4">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={isOpen ? `pv-detail-${p.produktversionId}` : undefined}
                  className="inline-flex items-center gap-1.5 font-mono text-xs text-accent-deep"
                  onClick={() => setOpen(isOpen ? null : p.produktversionId)}
                >
                  {isOpen ? t("detail.collapse") : t("detail.expand")}
                  <IconChevron
                    className={`h-[15px] w-[15px] transition-transform duration-300 ease-ease ${isOpen ? "-rotate-180" : ""}`}
                  />
                </button>
                <label
                  className={`flex select-none items-center gap-2 font-mono text-xs ${
                    kanVaelges ? "cursor-pointer text-ink-soft" : "cursor-not-allowed text-ink-faint opacity-60"
                  }`}
                  title={kanVaelges ? undefined : t("sam.maks", { n: MAX_VALGTE })}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--accent)]"
                    checked={valgt}
                    disabled={!kanVaelges}
                    onChange={() => toggleValg(p.produktversionId)}
                  />
                  {valgt && (
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ background: `var(--serie-${(valgtSlots.get(p.produktversionId) ?? 0) + 1})` }}
                    />
                  )}
                  {t("sam.vaelg")}
                </label>
              </div>
            </article>
            {egenEfterDenne && <EgenAftaleBox egen={egen} onSave={saveEgen} onRemove={removeEgen} />}
            </Fragment>
          );
        })}
      </div>

      {skjulte > 0 && (
        <button
          type="button"
          className="btn btn-ghost mx-auto mt-4 flex"
          onClick={() => setVisAlle(true)}
        >
          {t("results.visFlere", { n: skjulte })}
          <IconChevron className="h-[17px] w-[17px]" />
        </button>
      )}

      {fejlFor && <FejlModal produkt={fejlFor} onClose={() => setFejlFor(null)} />}

      <p className="mt-5 text-center font-mono text-[11.5px] leading-relaxed text-ink-faint">{t("results.disc")}</p>


      {visSammenligning && rates && (
        <Compare
          valgte={rows.filter((r) => valgtSlots.has(r.produkt.produktversionId))}
          profil={profil}
          satser={ratesToSatser(rates)}
          rates={rates}
          state={state}
          slots={valgtSlots}
          onClose={() => setVisSammenligning(false)}
        />
      )}
    </div>
  );
}

const Tag = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-px font-mono text-[11px]">
    {children}
  </span>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-3 border-b border-dotted border-line pb-1.5">
    <span className="text-ink-faint">{label}</span>
    <span className="num text-right text-[12.5px]">{value}</span>
  </div>
);
